import { appConfig } from '../config/appConfig';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { getAccessToken } from './authService';

// Types
export interface Prompt {
  id: number;
  title: string;
  promptText: string;
  description?: string;
  submittedBy: string;
  createdAt: string;
  updatedAt: string;
  categoryId: number;
  category: Category;
  tags: Tag[];
  rating?: number;
  ratingCount?: number;
  uses?: number;
  likes?: number;
  comments?: number;
  isLikedByCurrentUser?: boolean;
  isOptimistic?: boolean;
  qualityScore?: number | null;
  qualityAnalyzedAt?: string | null;
  qualityAnalysisVersion?: string | null;
}

// Prompt quality analysis (FR-7.2) — mirrors backend PromptQualityAnalysisDto
export interface QualitySubScores {
  clarity: number;
  specificity: number;
  outputFormat: number;
  constraints: number;
  tokenEfficiency: number;
}

export interface QualityFeedbackItem {
  phrase: string;
  reason: string;
  severity: 'High' | 'Medium' | 'Low';
  category: string;
  startOffset: number;
  endOffset: number;
}

export interface PromptQualityAnalysis {
  analysisId: string;
  analysisVersion: string;
  qualityScore: number;
  subScores: QualitySubScores;
  feedback: QualityFeedbackItem[];
  suggestedRewrite: string | null;
  expectedScoreRange: string;
  contentHash: string;
}

export interface AnalyzePromptRequest {
  title: string;
  promptText: string;
  categoryId: number;
  tags: string[];
}

export interface QualityConfig {
  autoApproveThreshold: number;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  color: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  isActive: boolean;
  rowVersion?: string;
}

export interface UpdateCategoryDto {
  name: string;
  description?: string;
  color: string;
  rowVersion: string;
}

export interface CategoryAuditDto {
  id: number;
  categoryId: number;
  action: 'CREATE' | 'UPDATE' | 'DEACTIVATE' | 'DELETE';
  changedBy: string;
  changedAt: string;
  beforeValues?: string;
  afterValues?: string;
  changeReason?: string;
}

export interface CategoryValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface CreateCategoryDto {
  name: string;
  description?: string;
  color: string;
}

export interface Tag {
  id: number;
  name: string;
  createdAt: string;
}

// Advanced Search Types
export interface AdvancedSearchCriteria {
  // Basic filters
  searchTerm?: string;
  categoryId?: number;
  categoryIds?: number[];
  tags?: string[];
  excludeTags?: string[];
  
  // Date filters
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  
  // Metrics filters
  minUses?: number;
  maxUses?: number;
  minLikes?: number;
  maxLikes?: number;
  minComments?: number;
  maxComments?: number;
  minRating?: number;
  
  // Content filters
  minContentLength?: number;
  maxContentLength?: number;
  submittedBy?: string;
  
  // Search options
  scope?: 'all' | 'title' | 'content' | 'description' | 'tags';
  exactMatch?: boolean;
  categoryMode?: 'any' | 'all';
  tagMode?: 'any' | 'all';
  
  // Pagination and sorting
  page?: number;
  pageSize?: number;
  sort?: string;
  sortBy?: string[];
  
  // Other options
  includeFacets?: boolean;
}

export interface RangeInfo {
  min: number;
  max: number;
  average: number;
}

export interface SearchFacets {
  categories: Record<string, number>;
  tags: Record<string, number>;
  submitters: Record<string, number>;
  usesRange: RangeInfo;
  likesRange: RangeInfo;
  ratingRange: RangeInfo;
  contentLengthRange: RangeInfo;
}

export interface SearchResult extends PagedResult {
  facets?: SearchFacets;
  searchDurationMs: number;
  searchId?: string;
}

export interface SavedSearch {
  id: number;
  name: string;
  description: string;
  searchCriteria: AdvancedSearchCriteria;
  createdAt: string;
  lastUsedAt: string;
  useCount: number;
  isPublic: boolean;
}

// Analytics Types
export interface OverviewStatsDto {
  totalPrompts: number;
  totalUsers: number;
  averageRating: number;
  totalUses: number;
  totalLikes: number;
  totalComments: number;
  totalPromptsChange: string;
  totalUsersChange: string;
  totalUsesChange: string;
  totalLikesChange: string;
  averageRatingChange: string;
  totalCommentsChange: string;
}

export interface UsageTrendDto {
  period: string;
  uses: number;
  submissions: number;
  likes: number;
  date: string;
}

export interface TopPromptDto {
  id: number;
  title: string;
  category: string;
  uses: number;
  likes: number;
  rating: number;
  ratingCount: number;
  submittedBy: string;
  createdAt: string;
  usesChange: string;
  likesChange: string;
}

export interface CategoryPerformanceDto {
  categoryId: number;
  categoryName: string;
  categoryColor: string;
  promptCount: number;
  totalUses: number;
  totalLikes: number;
  averageRating: number;
  usagePercentage: number;
  growthRate: string;
}

export interface AnalyticsDashboardDto {
  overviewStats: OverviewStatsDto;
  weeklyTrends: UsageTrendDto[];
  topPrompts: TopPromptDto[];
  categoryPerformance: CategoryPerformanceDto[];
}

// Prompt Quality Analytics Types (FR-7.4)
export interface QualityGroupAverageDto {
  id: number;
  name: string;
  scoredPromptCount: number;
  averageScore: number | null;
}

export interface QualityScoreBucketDto {
  bucket: string;
  minScore: number;
  maxScore: number;
  count: number;
}

export interface QualityTrendPointDto {
  month: string; // "yyyy-MM"
  scoredPromptCount: number;
  averageScore: number | null;
}

export interface QualitySubScoreAveragesDto {
  clarity: number | null;
  specificity: number | null;
  outputFormat: number | null;
  constraints: number | null;
  tokenEfficiency: number | null;
}

export interface QualityFeedbackCategoryCountDto {
  category: string;
  promptCount: number;
}

export interface PromptQualityAnalyticsDto {
  scoredPromptCount: number;
  totalPromptCount: number;
  overallAverageScore: number | null;
  averageScoreByTag: QualityGroupAverageDto[];
  averageScoreByCategory: QualityGroupAverageDto[];
  scoreDistribution: QualityScoreBucketDto[];
  improvementTrend: QualityTrendPointDto[];
  subScoreAverages: QualitySubScoreAveragesDto;
  topFeedbackCategories: QualityFeedbackCategoryCountDto[];
}

// Adoption Analytics Types (FR-8.2)
export interface AdoptionOverviewDto {
  totalPromptsCreated: number;
  totalViewsAndCopies: number;
  activeContributors: number;
  activeConsumers: number;
}

export interface AdoptionContributorDto {
  userId: string;
  displayName: string | null;
  email: string | null;
  contributionCount: number;
  promptsCreated: number;
  promptsEdited: number;
  averageQualityScore: number | null;
}

export interface AdoptionConsumerDto {
  userId: string;
  displayName: string | null;
  email: string | null;
  consumptionCount: number;
  views: number;
  copies: number;
}

export interface AdoptionTrendPointDto {
  periodStart: string;
  activeContributors: number;
  activeConsumers: number;
}

export interface UserAdoptionEventDto {
  eventType: string;
  promptId: number | null;
  promptTitle: string | null;
  timestamp: string;
}

export interface AdoptionDateRange {
  startDate: string; // "yyyy-MM-dd" or '' for open-ended
  endDate: string;
}

export interface PagedResult {
  items: Prompt[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Review Moderation Types
export type ReviewStatus = 0 | 1 | 2 | 3; // Pending=0, Approved=1, Rejected=2, Flagged=3

export interface ReviewDto {
  id: number;
  promptId: number;
  userId: string;
  reviewerName: string;
  content: string;
  title?: string;
  status: ReviewStatus;
  helpfulVotes: number;
  unhelpfulVotes: number;
  totalVotes: number;
  helpfulnessPercentage: number;
  createdAt: string;
  updatedAt: string;
  moderatorUserId?: string;
  moderatedAt?: string;
}

export interface ReviewPagedResultDto {
  reviews: ReviewDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface RatingAggregateDto {
  promptId: number;
  averageRating: number | null;
  totalRatings: number;
  ratingDistribution: Record<number, number>;
  userRating?: number | null;
}

export interface RatingDto {
  id: number;
  promptId: number;
  userId: string;
  score: number;
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentDto {
  id: number;
  promptId: number;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  sha256Hash: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export interface AttachmentSasUrlDto {
  id: number;
  sasUrl: string;
  expiresAt: string;
}

// API service with enhanced error handling and authentication support
export const api = {
  baseUrl: appConfig.apiUrl,
  
  // Store MSAL instance and account for authenticated requests
  msalInstance: null as IPublicClientApplication | null,
  currentAccount: null as AccountInfo | null,

  /**
   * Initialize the API service with MSAL instance and account
   * Call this from App.tsx after MSAL is initialized
   */
  initialize(msalInstance: IPublicClientApplication | null, account: AccountInfo | null) {
    this.msalInstance = msalInstance;
    this.currentAccount = account;
  },

  /**
   * Base request method with authentication support
   * Automatically adds Authorization header if auth is enabled and token is available
   */
  async request<T>(endpoint: string, options: RequestInit & { silent404?: boolean } = {}): Promise<T> {
    const { silent404, ...fetchOptions } = options as RequestInit & { silent404?: boolean };
    const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${this.baseUrl}${normalized}`;

    console.log(`API Request: ${fetchOptions.method || 'GET'} ${url}`);

    // Get auth token if authentication is enabled. getAccessToken throws
    // AuthSessionExpiredError if no token could be acquired — let that
    // propagate rather than silently sending the request unauthenticated.
    let authHeaders: HeadersInit = {};
    if (appConfig.enableAuthentication && this.msalInstance && this.currentAccount) {
      const token = await getAccessToken(this.msalInstance, this.currentAccount);
      if (token) {
        authHeaders = { 'Authorization': `Bearer ${token}` };
      }
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...fetchOptions.headers,
        },
        ...fetchOptions,
      });

      console.log(`API Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        if (!(silent404 && response.status === 404)) {
          console.error(`API Error ${response.status}:`, errorText);
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.errors) {
            const validationErrors = Object.entries(errorJson.errors)
              .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
              .join('; ');
            errorMessage = `Validation Error: ${validationErrors}`;
          } else if (errorJson.error) {
            errorMessage = errorJson.error;
          } else if (errorJson.title) {
            errorMessage = `HTTP ${response.status}: ${errorJson.title}`;
          }
        } catch {
          // If error text is not JSON, use it as is
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      // Handle empty body responses (204 No Content, 205 Reset Content, or Content-Length: 0)
      const contentLength = response.headers.get('Content-Length');
      if (
        response.status === 204 ||
        response.status === 205 ||
        contentLength === '0'
      ) {
        return undefined as unknown as T;
      }

      const data = await response.json();
      console.log('API Response Data:', data);
      return data;
    } catch (error) {
      if (!(silent404 && (error as Error)?.message?.startsWith('HTTP 404'))) {
        console.error('API Request Failed:', error);
      }
      throw error;
    }
  },
  
  async searchPrompts(params: {
    searchTerm?: string;
    categoryId?: number;
    tags?: string[];
    page?: number;
    pageSize?: number;
    sort?: string;
    minRating?: number;
    submittedByUserId?: string;
  }): Promise<PagedResult> {
    const queryParams = new URLSearchParams();
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.categoryId) queryParams.append('categoryId', params.categoryId.toString());
    if (params.tags) params.tags.forEach(tag => queryParams.append('tags', tag));
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.minRating) queryParams.append('minRating', params.minRating.toString());
    if (params.submittedByUserId) queryParams.append('submittedByUserId', params.submittedByUserId);

    return this.request<PagedResult>(`/prompts?${queryParams}`);
  },
  
  async getCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categories');
  },

  async createCategory(data: CreateCategoryDto): Promise<Category> {
    return this.request<Category>('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getCategoryById(id: number): Promise<Category> {
    return this.request<Category>(`/categories/${id}`);
  },

  async updateCategory(id: number, data: UpdateCategoryDto): Promise<Category> {
    return this.request<Category>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deactivateCategory(id: number, reason?: string): Promise<void> {
    return this.request<void>(`/categories/${id}`, {
      method: 'DELETE',
      body: reason ? JSON.stringify({ reason }) : undefined,
    });
  },

  async validateCategoryName(name: string, excludeId?: number): Promise<CategoryValidationResult> {
    const params = new URLSearchParams({ name });
    if (excludeId) {
      params.append('excludeId', excludeId.toString());
    }
    const response = await this.request<{ isUnique: boolean; name: string }>(`/categories/check-name?${params}`);
    return {
      isValid: response.isUnique,
      errors: response.isUnique ? [] : [`Category name "${name}" is already in use`]
    };
  },

  async getCategoryAuditTrail(categoryId: number): Promise<CategoryAuditDto[]> {
    return this.request<CategoryAuditDto[]>(`/categories/${categoryId}/audit`);
  },
  
  async getTags(search?: string): Promise<Tag[]> {
    const queryParams = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<Tag[]>(`/tags${queryParams}`);
  },
  
  // FR-7.2: analyze draft prompt quality before save. Persists nothing; the
  // returned analysisId is cached server-side (10-min TTL) and passed back on save.
  async analyzePrompt(data: AnalyzePromptRequest): Promise<PromptQualityAnalysis> {
    return this.request<PromptQualityAnalysis>('/prompts/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getQualityConfig(): Promise<QualityConfig> {
    return this.request<QualityConfig>('/prompts/quality-config');
  },

  async createPrompt(data: {
    title: string;
    promptText: string;
    description?: string;
    submittedBy: string;
    categoryId: number;
    tags: string[];
    analysisId?: string;
  }): Promise<Prompt> {
    console.log('Creating prompt with data:', data);
    return this.request<Prompt>('/prompts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updatePrompt(id: number, data: Partial<any>): Promise<Prompt> {
    return this.request<Prompt>(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deletePrompt(id: number): Promise<void> {
    return this.request<void>(`/prompts/${id}`, {
      method: 'DELETE',
    });
  },

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>('/health');
  },

  async incrementUses(promptId: number): Promise<{ uses: number }> {
    return this.request<{ uses: number }>(`/prompts/${promptId}/use`, {
      method: 'POST',
    });
  },

  // Analytics endpoints
  async getAnalyticsDashboard(): Promise<AnalyticsDashboardDto> {
    return this.request<AnalyticsDashboardDto>('/analytics/dashboard');
  },

  async getOverviewStats(): Promise<OverviewStatsDto> {
    return this.request<OverviewStatsDto>('/analytics/overview');
  },

  async getUsageTrends(days: number = 7): Promise<UsageTrendDto[]> {
    return this.request<UsageTrendDto[]>(`/analytics/trends?days=${days}`);
  },

  async getTopPrompts(count: number = 10, rankBy: 'uses' | 'likes' = 'uses'): Promise<TopPromptDto[]> {
    return this.request<TopPromptDto[]>(`/analytics/top-prompts?count=${count}&rankBy=${rankBy}`);
  },

  async getCategoryPerformance(): Promise<CategoryPerformanceDto[]> {
    return this.request<CategoryPerformanceDto[]>('/analytics/category-performance');
  },

  async getPromptQualityAnalytics(): Promise<PromptQualityAnalyticsDto> {
    return this.request<PromptQualityAnalyticsDto>('/analytics/prompt-quality');
  },

  // Adoption analytics endpoints (FR-8.2)
  adoptionRangeQuery(range: AdoptionDateRange): string {
    const params = new URLSearchParams();
    if (range.startDate) params.set('startDate', range.startDate);
    if (range.endDate) params.set('endDate', range.endDate);
    const query = params.toString();
    return query ? `?${query}` : '';
  },

  async getAdoptionOverview(range: AdoptionDateRange): Promise<AdoptionOverviewDto> {
    return this.request<AdoptionOverviewDto>(`/analytics/adoption/overview${this.adoptionRangeQuery(range)}`);
  },

  async getAdoptionContributors(range: AdoptionDateRange): Promise<AdoptionContributorDto[]> {
    return this.request<AdoptionContributorDto[]>(`/analytics/adoption/contributors${this.adoptionRangeQuery(range)}`);
  },

  async getAdoptionConsumers(range: AdoptionDateRange): Promise<AdoptionConsumerDto[]> {
    return this.request<AdoptionConsumerDto[]>(`/analytics/adoption/consumers${this.adoptionRangeQuery(range)}`);
  },

  async getAdoptionTrends(range: AdoptionDateRange, interval: 'week' | 'month' = 'week'): Promise<AdoptionTrendPointDto[]> {
    const query = this.adoptionRangeQuery(range);
    const separator = query ? '&' : '?';
    return this.request<AdoptionTrendPointDto[]>(`/analytics/adoption/trends${query}${separator}interval=${interval}`);
  },

  async getUserAdoptionHistory(userId: string, range: AdoptionDateRange): Promise<UserAdoptionEventDto[]> {
    return this.request<UserAdoptionEventDto[]>(
      `/analytics/adoption/users/${encodeURIComponent(userId)}${this.adoptionRangeQuery(range)}`
    );
  },

  // Advanced Search functionality
  async advancedSearchPrompts(criteria: AdvancedSearchCriteria): Promise<SearchResult> {
    // Map frontend string values to backend integer enum values.
    // SearchScope: All=0, TitleOnly=1, ContentOnly=2, DescriptionOnly=3, TagsOnly=4
    // CategoryFilterMode / TagFilterMode: Any=0, All=1
    const scopeMap: Record<string, number> = { all: 0, title: 1, content: 2, description: 3, tags: 4 };
    const modeMap: Record<string, number> = { any: 0, all: 1 };
    const body = {
      ...criteria,
      scope: criteria.scope !== undefined ? (scopeMap[criteria.scope] ?? 0) : 0,
      categoryMode: criteria.categoryMode !== undefined ? (modeMap[criteria.categoryMode] ?? 0) : 0,
      tagMode: criteria.tagMode !== undefined ? (modeMap[criteria.tagMode] ?? 0) : 0,
    };
    return this.request<SearchResult>('/prompts/advanced-search', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getSearchSuggestions(partial: string): Promise<string[]> {
    return this.request<string[]>(`/prompts/search-suggestions?partial=${encodeURIComponent(partial)}`);
  },

  async getPopularSearchTerms(limit: number = 10): Promise<string[]> {
    return this.request<string[]>(`/prompts/popular-searches?limit=${limit}`);
  },

  // Saved searches (future implementation)
  async getSavedSearches(): Promise<SavedSearch[]> {
    return this.request<SavedSearch[]>('/prompts/saved-searches');
  },

  async saveSearch(search: SavedSearch): Promise<SavedSearch> {
    return this.request<SavedSearch>('/prompts/saved-searches', {
      method: 'POST',
      body: JSON.stringify(search),
    });
  },

  async deleteSavedSearch(id: number): Promise<void> {
    return this.request<void>(`/prompts/saved-searches/${id}`, {
      method: 'DELETE',
    });
  },

  async likePrompt(id: number): Promise<Prompt> {
    return this.request<Prompt>(`/prompts/${id}/like`, { method: 'POST' });
  },

  async unlikePrompt(id: number): Promise<Prompt> {
    return this.request<Prompt>(`/prompts/${id}/like`, { method: 'DELETE' });
  },

  async submitReview(data: { promptId: number; reviewerName: string; title?: string; content: string }): Promise<any> {
    return this.request<any>('/reviews', {
      method: 'POST',
      body: JSON.stringify({
        PromptId: data.promptId,
        ReviewerName: data.reviewerName,
        Title: data.title || null,
        Content: data.content,
        RatingId: null,
      }),
    });
  },

  // Review Moderation (admin only)
  async getPendingReviews(page: number = 1, pageSize: number = 20): Promise<ReviewPagedResultDto> {
    return this.request<ReviewPagedResultDto>(`/reviews/moderation/pending?page=${page}&pageSize=${pageSize}`);
  },

  async moderateReviews(reviewIds: number[], status: ReviewStatus): Promise<void> {
    return this.request<void>('/reviews/moderation/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ reviewIds, status }),
    });
  },

  async getRatingAggregate(promptId: number): Promise<RatingAggregateDto> {
    return this.request<RatingAggregateDto>(`/ratings/prompt/${promptId}/aggregate`);
  },

  async getUserRating(promptId: number): Promise<RatingDto | null> {
    try {
      return await this.request<RatingDto>(`/ratings/prompt/${promptId}/user`, { silent404: true });
    } catch (e: any) {
      if (e?.message?.startsWith('HTTP 404')) return null;
      throw e;
    }
  },

  async submitRating(promptId: number, score: number, userId: string): Promise<RatingDto> {
    return this.request<RatingDto>('/ratings', {
      method: 'POST',
      body: JSON.stringify({ promptId, userId, score }),
    });
  },

  async updateRating(ratingId: number, score: number): Promise<RatingDto> {
    return this.request<RatingDto>(`/ratings/${ratingId}`, {
      method: 'PUT',
      body: JSON.stringify({ score }),
    });
  },

  async getAttachments(promptId: number): Promise<AttachmentDto[]> {
    return this.request<AttachmentDto[]>(`/prompts/${promptId}/attachments`);
  },

  async uploadAttachment(promptId: number, file: File): Promise<AttachmentDto> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.baseUrl}/prompts/${promptId}/attachments`;
    const authHeaders: Record<string, string> = {};
    if (appConfig.enableAuthentication && this.msalInstance && this.currentAccount) {
      const token = await getAccessToken(this.msalInstance, this.currentAccount);
      if (token) authHeaders['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'POST', headers: authHeaders, body: formData });
    if (!response.ok) {
      const text = await response.text();
      let msg = `HTTP ${response.status}: ${response.statusText}`;
      try { const j = JSON.parse(text); if (j.error) msg = j.error; } catch { if (text) msg = text; }
      throw new Error(msg);
    }
    return response.json();
  },

  async getAttachmentSasUrl(promptId: number, attachmentId: number): Promise<AttachmentSasUrlDto> {
    return this.request<AttachmentSasUrlDto>(`/prompts/${promptId}/attachments/${attachmentId}/sas`);
  },

  async deleteAttachment(promptId: number, attachmentId: number): Promise<void> {
    return this.request<void>(`/prompts/${promptId}/attachments/${attachmentId}`, { method: 'DELETE' });
  },
};